// src/pages/MsoRuleEditorPage.tsx
//
// ★★ ANNEX A 22 — one MSO rule VERSION: edit it while it is a draft, test it, and (as a second PSD
// officer) activate it; read it, and retire the rule, once it is in force.
//
// ══ THE LIFECYCLE THIS SCREEN WALKS, AND WHERE EACH GUARD LIVES ══
//
//   draft  ->  edit  ->  Save  ->  Run tests  ->  [second officer] Activate  ->  in force
//
// * **Edit** applies only to a DRAFT. An active, superseded or retired version is shown read-only:
//   a rule that has priced bills is history, and a correction is a NEW version.
// * **Test evidence is bound to the definition hash.** Editing a tested draft voids the evidence,
//   so Activate asks for a fresh run, and the panel shows the run only against the hash it ran on.
// * **Maker-checker.** The officer who created or last edited a draft cannot activate it. The
//   reason is shown on the Activate panel before the button is pressed; the route and the
//   database refuse regardless.
// * **Unsaved edits are never tested or activated by accident**: Run tests and Activate both
//   refuse while the form differs from what is saved, and say so.
//
// * **A save never overwrites someone else's change.** It carries the definition hash the editor LOADED
//   (`expectedHash`); if the draft has moved on, the route answers 409 STALE_EDIT, the page shows that message
//   and offers "Reload the draft", and nothing is saved.
//
// ⚠ A save does not need confirming (a draft prices nothing). Activate and Retire do, via
// `ConfirmWrite`, in `MsoActivationPanel`.
import { plainProse, plainRuleProblem } from '../lib/plain-words'
import { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router'
import { SgdsButton } from '@govtechsg/sgds-web-component/react'
import { api, ApiError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useAuth } from '../state/AuthContext'
import { canonicalText } from '../lib/mso-canonical'
import { unplacedErrors } from '../lib/mso-editor'
import { formatIsoDate } from '../lib/format'
import { MsoAuditEvidence } from '../components/MsoAuditEvidence'
import { isSeededVersion, MsoStatusBadge, personText } from '../components/MsoVersionTimeline'
import { ErrorCard, LoadingCard } from '../components/StateCards'
import { FormResult, type FormResultState } from '../components/FormResult'
import { DefinitionView } from '../components/MsoDefinitionView'
import { MsoDefinitionEditor } from '../components/MsoDefinitionEditor'
import { MsoActivationPanel, MsoRetirePanel } from '../components/MsoActivationPanel'
import { MsoTestPanel } from '../components/MsoTestPanel'
import { useCrumbTitle } from '../lib/crumbs'
import type { MsoCaseResult, MsoFieldBounds, MsoRuleDefinition, MsoRuleVersionResponse, MsoTestRun } from '../lib/types'

export function MsoRuleEditorPage() {
  const { id } = useParams()
  if (id === undefined) return <ErrorCard title="No version selected" message="Open a version from the MSO pro-ration rules list." />
  return <MsoRuleEditorLoader key={id} id={id} />
}

/** What an action said when it finished. It lives ABOVE the editor body, which is re-keyed (and so remounted) by a save. */
interface Notice {
  readonly message: string
  readonly id: number
  /** Offer a Run tests now button beside the message (after a save). */
  readonly runTests?: boolean
}

/**
 * ★ F-03: the result of Save draft, Activate and Retire. A save changes what is stored, which remounts the editor,
 * so the pressed button is gone and (being clean) would be disabled anyway. The message is a visible `role="status"`
 * and takes focus, so a keyboard user lands on what happened and not at the top of the page.
 */
function ResultNotice({ notice, onRunTests, running }: { notice: Notice | null; onRunTests: () => void; running: boolean }) {
  const ref = useRef<HTMLParagraphElement>(null)
  useEffect(() => {
    if (notice !== null) ref.current?.focus()
  }, [notice])
  if (notice === null) return null
  return (
    <>
    <p ref={ref} tabIndex={-1} role="status" aria-atomic="true" className="pt-card text-sm m-0 font-semibold text-[var(--sgds-success-color-emphasis)]" data-testid="result-notice">
      {plainProse(notice.message)}
    </p>
    {notice.runTests === true && (
      <div>
        <SgdsButton type="button" variant="primary" disabled={running} loading={running} onClick={onRunTests}>
          Run tests now
        </SgdsButton>
      </div>
    )}
    </>
  )
}

function MsoRuleEditorLoader({ id }: { id: string }) {
  const detail = useApi(() => api.msoRuleVersion(id), [id])
  const [notice, setNotice] = useState<Notice | null>(null)
  const announce = (message: string, runTests?: boolean) => setNotice({ message, id: Date.now(), ...(runTests === true && { runTests: true }) })
  useCrumbTitle(detail.data === undefined ? null : `${detail.data.version.ruleKey} v${detail.data.version.version}`)
  // The server's published length bounds, read once for the page. Their absence (a failed or older response)
  // shows no bound rather than a guessed one.
  const rules = useApi(() => api.msoRules(), [])
  if (detail.error !== null) {
    return <ErrorCard title="Could not load this rule version" message={detail.error} {...(detail.retryable && { onRetry: detail.reload })} />
  }
  if (detail.initialLoading || detail.data === undefined) return <LoadingCard label="Loading the rule version" rows={4} />
  const { version } = detail.data
  // ⚠ Re-keyed on what is SAVED, so a successful save or activation resets the form to the stored
  // definition rather than leaving local state describing something the server no longer holds.
  return (
    <EditorBody
      key={`${version.id}:${version.definitionHash}:${version.effectiveFrom}:${version.status}`}
      detail={detail.data}
      reload={detail.reload}
      bounds={rules.data?.bounds}
      notice={notice}
      announce={announce}
    />
  )
}

function EditorBody({
  detail,
  reload,
  bounds,
  notice,
  announce,
}: {
  detail: MsoRuleVersionResponse
  reload: () => void
  bounds: MsoFieldBounds | undefined
  notice: Notice | null
  announce: (message: string, runTests?: boolean) => void
}) {
  const { version, testCases, testRuns } = detail
  const runErrorRef = useRef<HTMLParagraphElement>(null)
  const { user } = useAuth()
  // ⚠ NOT `user.id`: `/api/me` reports the SESSION id there, which never equals the user id a draft records.
  const viewer = user === null ? null : { userId: user.userId ?? null, displayName: user.displayName }
  const editable = version.status === 'DRAFT'

  const [definition, setDefinition] = useState<MsoRuleDefinition>(version.definition)
  const [effectiveFrom, setEffectiveFrom] = useState(version.effectiveFrom)
  const [errors, setErrors] = useState<readonly string[]>([])
  const [saveState, setSaveState] = useState<FormResultState>({ status: 'idle' })
  const [lastRun, setLastRun] = useState<{ run: MsoTestRun; results: readonly MsoCaseResult[] } | null>(null)
  const [running, setRunning] = useState(false)
  const [runError, setRunError] = useState<string | null>(null)
  const [dateProblem, setDateProblem] = useState<string | null>(null)
  const [stale, setStale] = useState(false)
  const [resultFocusSignal, setResultFocusSignal] = useState(0)

  const dirty = canonicalText(definition) !== canonicalText(version.definition) || effectiveFrom !== version.effectiveFrom

  const handleSave = async () => {
    setSaveState({ status: 'submitting' })
    setErrors([])
    setDateProblem(null)
    setStale(false)
    try {
      // A date-only edit sends no definition; and every save names the hash it was made against.
      const definitionChanged = canonicalText(definition) !== canonicalText(version.definition)
      await api.editMsoDraft(version.id, {
        ...(definitionChanged && { definition }),
        effectiveFrom,
        expectedHash: version.definitionHash,
      })
      setSaveState({ status: 'idle' })
      announce('Saved. Run the tests again: a saved change voids earlier test evidence.', true)
      reload()
    } catch (error) {
      if (error instanceof ApiError) {
        // each problem is placed by its `path` (the route words the message itself); an older response with only `errors` keeps its own prefix
        const problems = error.detail['problems']
        const list = Array.isArray(problems)
          ? (problems as unknown[]).flatMap((p) => {
              const o = p as { path?: unknown; message?: unknown }
              if (typeof o.message !== 'string') return []
              return [typeof o.path === 'string' && o.path !== '' ? o.path + ': ' + o.message : o.message]
            })
          : error.detail['errors']
        setErrors(Array.isArray(list) ? list.filter((e): e is string => typeof e === 'string') : [])
        const outcome = error.detail['outcome']
        if (outcome === 'STALE_EDIT') setStale(true)
        if (outcome === 'BACKDATED_OVER_PRICED') setDateProblem(error.message)
        // the problems are listed once, at the row each names; the summary must not repeat them
        const listed = Array.isArray(list) && list.length > 0
        setSaveState({ status: 'error', message: listed ? 'The draft was not saved. Fix what is marked above.' : plainRuleProblem(error.message) })
      } else {
        setSaveState({ status: 'error', message: 'Could not save the draft.' })
      }
    }
  }

  /** True when the run finished (pass or fail); false when it could not be run, so the panel does not announce a result. */
  const handleRun = async (): Promise<boolean> => {
    setRunning(true)
    setRunError(null)
    try {
      const outcome = await api.testMsoDraft(version.id)
      setLastRun({ run: outcome.run, results: outcome.results })
      reload()
      return true
    } catch (error) {
      setRunError(error instanceof ApiError ? error.message : 'Could not run the tests. Check your connection and run them again.')
      return false
    } finally {
      setRunning(false)
    }
  }
  useEffect(() => {
    if (runError !== null) runErrorRef.current?.focus()
  }, [runError])

  return (
    <div className="flex flex-col gap-6">
      <ResultNotice notice={notice} running={running} onRunTests={() => void handleRun().then((finished) => finished && setResultFocusSignal((n) => n + 1))} />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl mb-2">
            {version.ruleKey} version {version.version}
          </h1>
          <p className="flex flex-wrap items-center gap-3 text-sm text-[var(--sgds-body-color-subtle)] m-0">
            <MsoStatusBadge status={version.status} />
            <span>
              Effective from {formatIsoDate(version.effectiveFrom)}
              {version.effectiveUntil === null ? '' : ` until ${formatIsoDate(version.effectiveUntil)}`}
            </span>
            <span>
              Created by {personText(version.createdByName, version.createdBy, isSeededVersion(version))}. Last edited by{' '}
              {personText(version.lastEditedByName, version.lastEditedBy, isSeededVersion(version))}.
              {version.activatedAt !== null && ` Activated by ${personText(version.activatedByName, version.activatedBy, isSeededVersion(version))}.`}
            </span>
            <span>
              Definition hash <span className="pt-mono" title={version.definitionHash}>{version.definitionHash.slice(0, 12)}</span>
            </span>
          </p>
        </div>
        <Link to="/administration/mso-rules" className="text-sm pt-link-target">
          Back to the MSO pro-ration rules
        </Link>
      </div>

      {!editable && (
        <div className="pt-unbuilt">
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--sgds-color-subtle)]">Read only</p>
          <p className="mt-2 text-[var(--sgds-body-color-subtle)]">
            This version is {version.status.toLowerCase()}. A version that has priced bills is not edited: a correction is a
            new version, started from the rules list.
          </p>
        </div>
      )}

      {editable ? (
        <>
          <MsoDefinitionEditor
            definition={definition}
            onChange={setDefinition}
            effectiveFrom={effectiveFrom}
            onEffectiveFromChange={setEffectiveFrom}
            errors={errors}
            dateProblem={dateProblem}
            bounds={bounds}
          />
          {unplacedErrors(errors).length > 0 && (
            <ul role="alert" className="text-sm text-[var(--sgds-danger-color-emphasis)] list-disc pl-5 m-0">
              {unplacedErrors(errors).map((e) => (
                <li key={e}>{plainRuleProblem(e)}</li>
              ))}
            </ul>
          )}
          <FormResult state={saveState} />
          {stale && (
            <div>
              <SgdsButton
                type="button"
                variant="outline"
                onClick={() => {
                  setStale(false)
                  setSaveState({ status: 'idle' })
                  reload()
                }}
              >
                Reload the draft
              </SgdsButton>
              <p className="text-sm text-[var(--sgds-body-color-subtle)] mt-2 mb-0">
                Reloading discards what you typed here and shows the saved draft. Copy anything you want to keep first.
              </p>
            </div>
          )}
          <div className="flex items-center gap-3">
            <SgdsButton type="button" variant="primary" disabled={!dirty || saveState.status === 'submitting'} loading={saveState.status === 'submitting'} onClick={() => void handleSave()}>
              Save draft
            </SgdsButton>
            {dirty && <span className="text-sm text-[var(--sgds-body-color-subtle)]">You have unsaved changes.</span>}
          </div>
        </>
      ) : (
        <section className="pt-card" aria-label="Definition">
          <DefinitionView definition={version.definition} />
          {version.provenanceNote !== null && (
            <p className="text-sm mt-4">
              <span className="font-semibold">Where the figures came from: </span>
              {plainProse(version.provenanceNote)}
            </p>
          )}
        </section>
      )}

      <MsoTestPanel
        version={version}
        testCases={testCases}
        testRuns={testRuns}
        lastRun={lastRun}
        dirty={dirty}
        editable={editable}
        onRun={handleRun}
        running={running}
        onCasesChanged={reload}
        bounds={bounds}
        resultFocusSignal={resultFocusSignal}
      />
      {runError !== null && (
        <p ref={runErrorRef} tabIndex={-1} role="alert" className="text-sm text-[var(--sgds-danger-color-emphasis)]">
          {runError}
        </p>
      )}

      {editable && (
        <MsoActivationPanel
          version={version}
          viewer={viewer}
          testRuns={testRuns}
          dirty={dirty}
          onChanged={reload}
          onNotice={announce}
          bounds={bounds}
          warnings={detail.activationWarnings ?? []}
          makerCheckerNote={detail.makerCheckerNote ?? null}
          youCanActivate={detail.youCanActivate ?? null}
        />
      )}
      {version.status === 'ACTIVE' && <MsoRetirePanel version={version} onChanged={reload} onNotice={announce} bounds={bounds} />}

      <MsoAuditEvidence
        scopes={[{ resourceType: 'mso-rule-version', resourceId: version.id }]}
        intro="The audit events recorded for this version: drafts, edits, test runs and its activation."
      />
    </div>
  )
}
